import React, { useEffect, useState } from 'react';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { storage, db, auth } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, Trash2, ShieldCheck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';

interface Props {
  dealId: string;
}

const CATEGORIES = [
  { key: 'financial', labelEn: 'Financial', labelFr: 'Financier' },
  { key: 'certifications', labelEn: 'Certifications', labelFr: 'Certifications' },
  { key: 'safety', labelEn: 'Safety', labelFr: 'Sécurité' },
  { key: 'legal', labelEn: 'Legal', labelFr: 'Juridique' },
  { key: 'other', labelEn: 'Other', labelFr: 'Autre' },
];

interface UploadedFile {
  name: string;
  fullPath: string;
  category: string;
  url: string;
}

const SecureDocumentsSection: React.FC<Props> = ({ dealId }) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const t = {
    title: language === 'fr' ? 'Documents Sécurisés' : 'Secure Documents',
    upload: language === 'fr' ? 'Téléverser' : 'Upload',
    delete: language === 'fr' ? 'Supprimer' : 'Delete',
    noFiles: language === 'fr' ? 'Aucun document' : 'No documents',
    uploadSuccess: language === 'fr' ? 'Document téléversé' : 'Document uploaded',
    uploadError: language === 'fr' ? 'Échec du téléversement' : 'Upload failed',
    statusUploaded: language === 'fr' ? 'Téléversé' : 'Uploaded',
  };

  const loadFiles = async () => {
  try {
    setLoading(true);
    const merged = new Map<string, UploadedFile>();

    // 1) Source de vérité : Firestore mirror `dealDocuments`
    try {
      const q = query(
        collection(db, 'dealDocuments'),
        where('dealId', '==', dealId)
      );
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        const data = d.data() as any;
        if (!data.storagePath) continue;
        const cat = data.category || 'other';
        if (!CATEGORIES.find((c) => c.key === cat)) continue;
        let url = '';
        try {
          url = await getDownloadURL(ref(storage, data.storagePath));
        } catch (e) {
          console.warn('[SecureDocs] URL introuvable', data.storagePath, e);
          continue; // fichier orphelin en Firestore, on l'ignore
        }
        merged.set(data.storagePath, {
          name: data.fileName || data.storagePath.split('/').pop() || 'file',
          fullPath: data.storagePath,
          category: cat,
          url,
        });
      }
    } catch (e) {
      console.warn('[SecureDocs] Firestore mirror unavailable', e);
    }

    // 2) Fallback / complément : Storage direct (deals anciens sans mirror)
    for (const cat of CATEGORIES) {
      const folderRef = ref(storage, `deals/${dealId}/seller_documents/${cat.key}`);
      try {
        const res = await listAll(folderRef);
        for (const item of res.items) {
          if (merged.has(item.fullPath)) continue;
          try {
            const url = await getDownloadURL(item);
            merged.set(item.fullPath, {
              name: item.name,
              fullPath: item.fullPath,
              category: cat.key,
              url,
            });
          } catch (e) {
            console.warn('[SecureDocs] getDownloadURL failed', item.fullPath, e);
          }
        }
      } catch (e) {
        // Dossier absent ou refusé par rules : normal si le deal n'a jamais rien uploadé ici
        console.debug('[SecureDocs] listAll skip', folderRef.fullPath);
      }
    }

    setFiles(Array.from(merged.values()));
  } finally {
    setLoading(false);
  }
  };

  useEffect(() => {
    if (dealId) loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const handleUpload = async (category: string, file: File) => {
    if (!file) return;
    setUploading(category);
    try {
      const path = `deals/${dealId}/seller_documents/${category}/${Date.now()}_${file.name}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);

      // Mirror in Firestore for queryability
      await addDoc(collection(db, 'dealDocuments'), {
        dealId,
        category,
        fileName: file.name,
        storagePath: path,
        uploadedBy: auth.currentUser?.uid || null,
        uploadedAt: serverTimestamp(),
        status: 'uploaded',
      });

      // Admin task notification
      await addDoc(collection(db, 'admin_tasks'), {
        type: 'document_uploaded',
        dealId,
        category,
        fileName: file.name,
        uploadedBy: auth.currentUser?.uid || null,
        status: 'pending_review',
        createdAt: serverTimestamp(),
      });

      toast({ title: t.uploadSuccess });
      await loadFiles();
    } catch (e) {
      console.error(e);
      toast({ title: t.uploadError, variant: 'destructive' });
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (file: UploadedFile) => {
    try {
      await deleteObject(ref(storage, file.fullPath));
      await loadFiles();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" /> {t.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {CATEGORIES.map((cat) => {
          const catFiles = files.filter((f) => f.category === cat.key);
          return (
            <div key={cat.key} className="border rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">
                  {language === 'fr' ? cat.labelFr : cat.labelEn}
                </Label>
                <div className="relative">
                  <Input
                    type="file"
                    className="hidden"
                    id={`upload-${dealId}-${cat.key}`}
                    onChange={(e) => e.target.files?.[0] && handleUpload(cat.key, e.target.files[0])}
                    disabled={uploading === cat.key}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    asChild
                    disabled={uploading === cat.key}
                  >
                    <label htmlFor={`upload-${dealId}-${cat.key}`} className="cursor-pointer">
                      <Upload className="h-4 w-4 mr-1" />
                      {uploading === cat.key ? '...' : t.upload}
                    </label>
                  </Button>
                </div>
              </div>
              {catFiles.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t.noFiles}</p>
              ) : (
                <div className="space-y-1">
                  {catFiles.map((f) => (
                    <div key={f.fullPath} className="flex items-center justify-between text-sm bg-muted/40 rounded p-2">
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 hover:underline truncate"
                      >
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="truncate">{f.name}</span>
                      </a>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="secondary" className="text-xs">{t.statusUploaded}</Badge>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(f)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {loading && <p className="text-xs text-muted-foreground">Loading...</p>}
      </CardContent>
    </Card>
  );
};

export default SecureDocumentsSection;
